// frontend/src/PatientFiles.js
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import DSIRecommendationModal, {
  TREATMENT_RECOMMENDED,
  TREATMENT_NOT_RECOMMENDED,
} from './DSIRecommendationModal';
import './PatientFiles.css';

// ----- Generate dropdown options -----
const MAX_N = 10;
function range(n1, n2) {
  const out = [];
  for (let i = n1; i <= n2; i++) out.push(i);
  return out;
}

const FIXED_CODES = [
  'CON', 'CONPRESG', 'CONPOSTSG',
  'CFU', 'CFUSG',
  'PTFU', 'PTFUSG',
  'INS',
];
const DnE = range(2, MAX_N).map((n) => `D${n}E`);
const DnESG = range(2, MAX_N).map((n) => `D${n}ESG`);
const XDn = range(1, MAX_N).map((n) => `XD${n}`);
const XDnSG = range(1, MAX_N).map((n) => `XD${n}SG`);
const FRn = range(1, MAX_N).map((n) => `FR${n}`);

const TOOLTIP_KEY = 'dsi.tooltip';

const leaf = (pathOrUrl) => {
  if (!pathOrUrl) return '';
  const clean = String(pathOrUrl).split('?')[0];
  const parts = clean.split('/');
  return parts[parts.length - 1];
};

const getPortalFileName = (file) => {
  // Azure Blob naming is the source of truth after DSI upload processing.
  // The backend keeps original_filename for audit/history, but the portal should show
  // the renamed processing filename, for example LastnameFirstnameCON.tom.
  return (
    leaf(file?.blob_name) ||
    leaf(file?.blob_url) ||
    file?.filename ||
    file?.name ||
    file?.original_filename ||
    ''
  );
};

const POLL_MS = 10 * 60 * 1000;

function normalizeDateForPortal(value) {
  if (!value) return '';
  const raw = String(value).trim();

  const ymdDash = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdDash) return `${ymdDash[1]}/${ymdDash[2]}/${ymdDash[3]}`;

  const ymdSlash = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
  if (ymdSlash) return `${ymdSlash[1]}/${ymdSlash[2]}/${ymdSlash[3]}`;

  const mdySlash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdySlash) {
    const mm = String(mdySlash[1]).padStart(2, '0');
    const dd = String(mdySlash[2]).padStart(2, '0');
    return `${mdySlash[3]}/${mm}/${dd}`;
  }

  return raw;
}

function getAutoReportBase() {
  const envUrl =
    (typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_AUTOREPORT_URL) ||
    '';

  if (envUrl && String(envUrl).trim()) {
    return String(envUrl).trim().replace(/\/+$/, '');
  }

  const host = window.location.hostname.toLowerCase();

  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:5173';
  }

  return 'https://ip-autoreport-frontend.chinacloudsites.cn';
}

function getServiceCenterName(patient) {
  return (
    patient?.service_center_detail?.name ||
    patient?.service_center?.name ||
    patient?.service_center_name ||
    ''
  );
}

function buildAutoReportUrl({ patient, patientId, file }) {
  const base = getAutoReportBase();
  const params = new URLSearchParams();

  const firstName = patient?.first_name || '';
  const lastName = patient?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const serviceCenterName = String(getServiceCenterName(patient) || '');

  params.set('source', 'precision');
  params.set('patient_id', String(patientId || ''));
  params.set('first_name', firstName);
  params.set('last_name', lastName);
  params.set('name', fullName);
  params.set('dob', normalizeDateForPortal(patient?.date_of_birth || ''));
  params.set('dsi_date', normalizeDateForPortal(patient?.dsi_date || ''));
  params.set('clinic', serviceCenterName);
  params.set('distributor', serviceCenterName);
  params.set('service_center_name', serviceCenterName);
  params.set('processing_type', String(file?.processing_type || ''));
  params.set('file_name', String(getPortalFileName(file) || ''));
  params.set('return_url', window.location.href);

  const fileUrl =
    file?.blob_url ||
    file?.url ||
    file?.file_url ||
    file?.file ||
    file?.download_url ||
    '';

  if (fileUrl) {
    params.set('file_url', fileUrl);
  }

  return `${base}/?${params.toString()}`;
}

const PatientFiles = ({
  patientId,
  patient = null,
  hideSendForDSICheckbox = true,
  showDSIDropdownDefault = true,
  onRecommendationChange = null,
}) => {
  const { t, i18n } = useTranslation();
  const isZh = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh');
  const locale = isZh ? 'zh-CN' : 'en-US';
  const recommendationEnabled = patient?.dsi_recommendation_enabled === true;
  const [files, setFiles] = useState([]);
  const [fileUpload, setFileUpload] = useState(null);

  const [sendForProcessing, setSendForProcessing] = useState(
    showDSIDropdownDefault && !hideSendForDSICheckbox
  );
  const [processingType, setProcessingType] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [uploadStatus, setUploadStatus] = useState({
    state: 'idle',
    pct: 0,
    message: '',
  });

  const [pendingRecommendationFile, setPendingRecommendationFile] = useState(null);
  const [recommendationSaving, setRecommendationSaving] = useState(false);
  const [recommendationError, setRecommendationError] = useState('');
  const [currentRecommendation, setCurrentRecommendation] = useState({
    status: recommendationEnabled ? (patient?.latest_recommendation_status || '') : '',
    display: recommendationEnabled ? (patient?.latest_recommendation_display || '') : '',
    createdAt: recommendationEnabled ? (patient?.latest_recommendation_at || null) : null,
  });

  const pollRef = useRef(null);
  const reqRef = useRef(null);

  const clearStatusSoon = () => {
    setTimeout(() => setUploadStatus({ state: 'idle', pct: 0, message: '' }), 2500);
  };

  const fetchFiles = useCallback(async () => {
    try {
      if (reqRef.current) reqRef.current.abort?.();
      const controller = new AbortController();
      reqRef.current = controller;

      // Ask Django for both database-backed DSI uploads and historical TOM blobs.
      // The backend de-duplicates them, so older Azure-only files remain visible
      // without hiding newer FileUpload rows that carry recommendation metadata.
      const res = await axios.get(`patients/${patientId}/files/`, {
        params: { include_azure_toms: 1 },
        signal: controller.signal,
      });

      const raw = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.files)
          ? res.data.files
          : Array.isArray(res.data?.results)
            ? res.data.results
            : [];

      const onlyTomFiles = raw.filter((f) => {
        const displayName =
          f?.original_filename ||
          f?.filename ||
          f?.name ||
          f?.blob_name ||
          f?.blob_url ||
          '';

        const nm = leaf(displayName).toLowerCase();
        if (!nm.endsWith('.tom')) return false;

        // At this point the name is already confirmed to be a .tom. Do not
        // discard historical manufacturing-container TOMs: many legacy patient
        // records only have the durable Azure blob and no surviving FileUpload row.
        return true;
      });

      setFiles(onlyTomFiles);
      setError('');
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error(t('error.fetchFiles'), err);
      setError(t('error.failedLoadFiles') || 'Failed to load files.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, t]);

  useEffect(() => {
    fetchFiles();
    return () => {
      if (reqRef.current) reqRef.current.abort?.();
    };
  }, [fetchFiles]);

  useEffect(() => {
    pollRef.current = setInterval(fetchFiles, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchFiles]);

  useEffect(() => {
    setCurrentRecommendation({
      status: recommendationEnabled ? (patient?.latest_recommendation_status || '') : '',
      display: recommendationEnabled ? (patient?.latest_recommendation_display || '') : '',
      createdAt: recommendationEnabled ? (patient?.latest_recommendation_at || null) : null,
    });
  }, [
    patient?.latest_recommendation_status,
    patient?.latest_recommendation_display,
    patient?.latest_recommendation_at,
    recommendationEnabled,
  ]);

  const handleFileChange = (e) => setFileUpload(e.target.files?.[0] || null);

  function extractServerError(err) {
    const data = err?.response?.data;
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    if (data.detail) return data.detail;
    const firstKey = Object.keys(data)[0];
    const val = firstKey ? data[firstKey] : null;
    if (Array.isArray(val) && val.length > 0) return String(val[0]);
    if (typeof val === 'string') return val;
    return null;
  }

  const handleUpload = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError('');
    setUploadStatus({
      state: 'uploading',
      pct: 0,
      message: t('uploading', { defaultValue: 'Uploading…' }),
    });

    try {
      if (!fileUpload) {
        const msg = t('error.noFileSelected') || 'Please select a file.';
        setError(msg);
        setUploadStatus({ state: 'error', pct: 0, message: msg });
        clearStatusSoon();
        setSubmitting(false);
        return;
      }

      const actualSendForProcessing = hideSendForDSICheckbox
        ? Boolean(processingType)
        : Boolean(sendForProcessing);

      if ((hideSendForDSICheckbox || actualSendForProcessing) && !processingType) {
        const msg = t('error.processingTypeRequired') || 'Select a processing type.';
        setError(msg);
        setUploadStatus({ state: 'error', pct: 0, message: msg });
        clearStatusSoon();
        setSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', fileUpload);
      formData.append('send_for_processing', actualSendForProcessing ? 'true' : 'false');
      if (actualSendForProcessing && processingType) {
        formData.append('processing_type', processingType);
      }

      const uploadRes = await axios.post(`patients/${patientId}/files/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        onUploadProgress: (evt) => {
          const total = evt?.total;
          const loaded = evt?.loaded;
          if (!total || !loaded) return;
          const pct = Math.round((loaded / total) * 100);
          const clamped = Math.max(0, Math.min(100, pct));

          if (clamped >= 100) {
            setUploadStatus({
              state: 'waiting',
              pct: 100,
              message: t('waitingServer', { defaultValue: 'Upload sent — waiting for server…' }),
            });
          } else {
            setUploadStatus({
              state: 'uploading',
              pct: clamped,
              message: t('uploading', { defaultValue: 'Uploading…' }),
            });
          }
        },
      });

      const uploadedFile = uploadRes?.data || null;
      setFileUpload(null);
      if (!hideSendForDSICheckbox) setSendForProcessing(false);
      setProcessingType('');

      // The upload response is authoritative for whether this specific DSI needs
      // a recommendation. Show the popup immediately; do not make the user wait
      // for the slower historical-Azure refresh to complete first.
      if (actualSendForProcessing && uploadedFile?.id && uploadedFile?.recommendation_required) {
        setRecommendationError('');
        setPendingRecommendationFile(uploadedFile);
        setUploadStatus({
          state: 'waiting',
          pct: 100,
          message: t('dsi.uploadedChooseRecommendation'),
        });
      } else {
        setUploadStatus({
          state: 'success',
          pct: 100,
          message: actualSendForProcessing && uploadedFile && uploadedFile.recommendation_required === false
            ? t('dsi.oldPatientComplete')
            : t('uploadSuccess', { defaultValue: 'Upload successful.' }),
        });
        clearStatusSoon();
      }

      // Refresh the list after the UI has reacted to the upload response.
      // Azure compatibility discovery is allowed to fail independently because
      // the just-created upload is already durable in Django/Azure.
      try {
        await fetchFiles();
      } catch (_) {}
    } catch (err) {
      if (!err?.response?.status) {
        const msg =
          t('dsi.uploadInterrupted');
        setUploadStatus({ state: 'error', pct: 100, message: msg });
        clearStatusSoon();
        try {
          await fetchFiles();
        } catch (_) {}
      } else {
        const serverMessage = extractServerError(err);
        const msg = (!isZh && serverMessage) ? serverMessage : t('error.failedUploadFile');
        setError(msg);
        setUploadStatus({ state: 'error', pct: 100, message: msg });
        clearStatusSoon();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecommendationSave = async (statusValue) => {
    if (!pendingRecommendationFile?.id || recommendationSaving) return;

    setRecommendationSaving(true);
    setRecommendationError('');
    try {
      const res = await axios.post(
        `patients/${patientId}/files/${pendingRecommendationFile.id}/recommendation/`,
        { status: statusValue }
      );

      const savedFile = res?.data?.file || {};
      const rec = res?.data?.recommendation || {};
      const display = rec.status_display || (
        statusValue === TREATMENT_RECOMMENDED
          ? t('recommendation.recommended')
          : t('recommendation.notRecommended')
      );
      const createdAt = rec.created_at || new Date().toISOString();

      setFiles((prev) => prev.map((item) =>
        String(item.id) === String(savedFile.id || pendingRecommendationFile.id)
          ? { ...item, ...savedFile }
          : item
      ));
      setCurrentRecommendation({ status: statusValue, display, createdAt });
      onRecommendationChange?.({
        latest_recommendation_status: statusValue,
        latest_recommendation_display: display,
        latest_recommendation_at: createdAt,
      });

      setPendingRecommendationFile(null);
      setUploadStatus({
        state: 'success',
        pct: 100,
        message: t('dsi.completeSaved'),
      });
      clearStatusSoon();
      await fetchFiles();
    } catch (err) {
      const serverMessage = extractServerError(err);
      const msg = (!isZh && serverMessage) ? serverMessage : t('dsi.recommendationSaveError');
      setRecommendationError(msg);
      setUploadStatus({ state: 'error', pct: 100, message: msg });
    } finally {
      setRecommendationSaving(false);
    }
  };

  const groupedOptions = useMemo(
    () => [
      { label: t('dsi.group.fixed'), values: FIXED_CODES },
      { label: t('dsi.group.bandEvaluation'), values: DnE },
      { label: t('dsi.group.surgicalBandEvaluation'), values: DnESG },
      { label: t('dsi.group.exit'), values: XDn },
      { label: t('dsi.group.surgicalExit'), values: XDnSG },
      { label: t('dsi.group.forReview'), values: FRn },
    ],
    [t]
  );

  const showStatusBlock = uploadStatus.state !== 'idle';

  return (
    <div className="pfm-container">
      <h3 className="pfm-title">{t('dsi.title')}</h3>

      {recommendationEnabled && (
        <div className={`pfm-rec-summary ${currentRecommendation.status ? 'has-value' : 'is-empty'}`}>
          <div>
            <span className="pfm-rec-label">{t('dsi.currentRecommendation')}</span>
            <strong>{currentRecommendation.status
              ? (currentRecommendation.status === TREATMENT_RECOMMENDED ? t('recommendation.recommended') : t('recommendation.notRecommended'))
              : t('dsi.noRecommendation')}</strong>
          </div>
          {currentRecommendation.status && (
            <span className={`pfm-rec-mark ${currentRecommendation.status === TREATMENT_RECOMMENDED ? 'is-recommended' : 'is-not-recommended'}`}>
              {currentRecommendation.status === TREATMENT_RECOMMENDED ? t('dsi.recommendedShort') : t('dsi.notRecommendedShort')}
            </span>
          )}
        </div>
      )}

      <form onSubmit={handleUpload} className="pfm-form">
        <div className="pfm-field" style={{ gridColumn: '1 / -1' }}>
          <label>{t('selectFile') || 'Select file'}</label>
          <input
            type="file"
            onChange={handleFileChange}
            className="pfm-input"
            accept=".tom,application/octet-stream"
          />
        </div>

        {!hideSendForDSICheckbox && (
          <div className="pfm-inline" style={{ gridColumn: '1 / -1' }}>
            <input
              id="sendForProcessing"
              type="checkbox"
              checked={sendForProcessing}
              onChange={(e) => setSendForProcessing(e.target.checked)}
            />
            <label htmlFor="sendForProcessing">{t('dsi.sendForReport')}</label>
          </div>
        )}

        {(hideSendForDSICheckbox || sendForProcessing) && (
          <div className="pfm-field" style={{ gridColumn: '1 / -1' }}>
            <div className="pfm-label-row">
              <label>{t('dsi.processingType')}</label>
              <span className="pf-help" title={t(TOOLTIP_KEY)}>!</span>
            </div>

            <select
              className="pfm-select"
              value={processingType}
              onChange={(e) => setProcessingType(e.target.value)}
              required={hideSendForDSICheckbox || sendForProcessing}
            >
              <option value="">{t('dsi.selectProcessing')}</option>

              {groupedOptions.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.values.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        )}

        {showStatusBlock && (
          <div className="pfm-status" style={{ gridColumn: '1 / -1' }}>
            {uploadStatus.state === 'uploading' || uploadStatus.state === 'waiting' ? (
              <div className="pfm-progress">
                <div className="pfm-progress-track">
                  <div
                    className="pfm-progress-bar"
                    style={{ width: `${uploadStatus.pct || 0}%` }}
                  />
                </div>
                <div className="pfm-progress-text">
                  {uploadStatus.message} {uploadStatus.pct}%
                </div>
              </div>
            ) : (
              <div
                className={`pfm-toast-inline ${
                  uploadStatus.state === 'success'
                    ? 'pfm-toast-inline--success'
                    : 'pfm-toast-inline--error'
                }`}
                role="status"
                aria-live="polite"
              >
                {uploadStatus.message}
              </div>
            )}
          </div>
        )}

        {error && <div className="pfm-error">{error}</div>}

        <div className="pfm-actions">
          <button type="submit" className="pfm-btn" disabled={submitting}>
            {submitting ? (t('uploading') || 'Uploading…') : (t('upload') || 'Upload')}
          </button>
        </div>
      </form>

      {loading ? (
        <p>{t('loading.files') || 'Loading files...'}</p>
      ) : (
        <ul className="pf-list">
          {files.map((file) => {
            const fileLeaf = getPortalFileName(file);

            const fileHref =
              file?.blob_url ||
              file?.url ||
              file?.file_url ||
              file?.file ||
              file?.download_url ||
              '';

            const dateStr = file.created_at ? new Date(file.created_at).toLocaleString(locale) : '';

            const key =
              file.id ||
              file.blob_name ||
              file.blob_url ||
              file.filename ||
              `${fileLeaf}-${dateStr}`;

            const runReportUrl = buildAutoReportUrl({ patient, patientId, file });

            return (
              <li key={key} className="pf-item">
                {file.processing_type && (
                  <span className="pf-tag">{file.processing_type}</span>
                )}

                {file.treatment_recommendation ? (
                  <span
                    className={`pf-rec-tag ${file.treatment_recommendation === TREATMENT_RECOMMENDED ? 'is-recommended' : 'is-not-recommended'}`}
                    title={file.treatment_recommendation === TREATMENT_RECOMMENDED ? t('recommendation.recommended') : t('recommendation.notRecommended')}
                  >
                    {file.treatment_recommendation === TREATMENT_RECOMMENDED ? t('dsi.recommendedShort') : t('dsi.notRecommendedShort')}
                  </span>
                ) : file.recommendation_required && file.id && !String(file.id).startsWith('azure:') ? (
                  <button
                    type="button"
                    className="pf-rec-pending"
                    onClick={() => { setRecommendationError(''); setPendingRecommendationFile(file); }}
                  >
                    {t('dsi.recommendationRequired')}
                  </button>
                ) : null}

                <div className="pf-links">
                  {fileHref ? (
                    <a href={fileHref} target="_blank" rel="noreferrer">
                      {fileLeaf}
                    </a>
                  ) : (
                    <span className="pf-filename-fallback">{fileLeaf}</span>
                  )}
                </div>

                {file.recommendation_required && !file.treatment_recommendation ? (
                  <span className="pf-run-report-link pf-run-report-link--disabled" title={t('dsi.completeRecommendationTitle')}>
                    {t('dsi.completeRecommendationFirst')}
                  </span>
                ) : (
                  <a
                    href={runReportUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="pf-run-report-link"
                    title={t('files.openAutoReport')}
                  >
                    {t('dsi.runReport')}
                  </a>
                )}

                <span className="pf-date">
                  {t('uploadedOn') || 'Uploaded on'}: {dateStr}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <DSIRecommendationModal
        open={!!pendingRecommendationFile}
        patient={patient}
        file={pendingRecommendationFile}
        saving={recommendationSaving}
        error={recommendationError}
        onSave={handleRecommendationSave}
      />
    </div>
  );
};

export default PatientFiles;